import type { EmailSendRuleDto } from '@/types/touchPoint'
import { EmailComplianceClass, EmailEventType, EmailSendRuleKind } from '@/types/touchPoint'

/**
 * Client mirror of the server's compliance-class default
 * (OrbitalX.Business/Services/EmailRuleCatalogService.cs → GetDefaultComplianceClass).
 *
 * Used to render the resolved "Auto" consent chip in the activation editor so the user can see the
 * effective class without an explicit override. The server stays authoritative — it re-derives this
 * on save whenever ComplianceClass is null — so this MUST stay in lock-step with that switch.
 *
 * Per the repo no-fallbacks norm: an unmapped anchor is a programming error surfaced loudly, never a
 * silent Marketing catch-all. Callers that render UI must guard the throw (a malformed/legacy rule
 * shouldn't crash the panel).
 */
export function resolveComplianceClass(rule: EmailSendRuleDto): EmailComplianceClass {
  // The Scheduled (absolute-time) rule is the cold promotional blast — Marketing by default.
  if (rule.Kind === EmailSendRuleKind.Scheduled) {
    return EmailComplianceClass.Marketing
  }

  switch (rule.EventType) {
    // Registrant/booker-anchored mail reaches only the people who opted in by registering, booking or
    // downloading — the confirmation, the registrant-scoped pre-event reminder, and the recording for
    // people who missed the event. Transactional: sent to the people who signed up.
    case EmailEventType.WebRegistered:
    case EmailEventType.AssetDownloaded:
    case EmailEventType.MeetingBooked:
    case EmailEventType.DemoBooked:
    case EmailEventType.OnlineEventMissed:
    case EmailEventType.OnlineEventStartRegistrants:
      return EmailComplianceClass.Transactional

    // ORB-924. Attendees opted in too, but what follows attendance is a sales follow-up, not something
    // they signed up to receive. Marketing, so it honors opt-outs and carries the unsubscribe footer.
    case EmailEventType.OnlineEventAttended:
      return EmailComplianceClass.Marketing

    // ORB-536. The inverse of the registrant reminder above, on the same instant: these target the
    // cold imported audience MINUS everyone who registered — i.e. the people who have NOT opted in —
    // and their CTA is "register". A marketing cadence to a non-consented list, so Marketing.
    case EmailEventType.OnlineEventStart:
    case EmailEventType.OnlineEventEnd:
      return EmailComplianceClass.Marketing

    // ORB-508. The click nudge is per-recipient like the transactional block above, but the recipient
    // has pointedly NOT opted in — a click is not consent, and the anchor only ever fires for someone
    // with no Lead. Marketing, so it honors opt-outs and carries the unsubscribe footer.
    case EmailEventType.EmailClicked:
      return EmailComplianceClass.Marketing

    // No silent Marketing catch-all: an unmapped anchor is a programming error, surfaced loudly.
    default:
      throw new Error(`No default compliance class for event type '${rule.EventType}'.`)
  }
}

/**
 * True when this rule's audience opted in, and therefore MAY be sent Transactional (ORB-508).
 *
 * Transactional bypasses the per-company opt-out list, so the server rejects it on any rule whose
 * default class is Marketing — the cold blast, the pre-event reminders that target non-registrants,
 * and the click nudge. Reading the answer off the default keeps the two in lock-step instead of
 * maintaining a second list. A malformed rule reports false: offering an option the server will
 * refuse is worse than hiding one it would have accepted.
 */
export function isTransactionalAllowed(rule: EmailSendRuleDto): boolean {
  try {
    return resolveComplianceClass(rule) === EmailComplianceClass.Transactional
  } catch {
    return false
  }
}
